import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { setJsonLd, setPageMeta } from "@/lib/api";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { ProductCard } from "@/components/ProductCard";
import { Reveal } from "@/components/Motion";
import { collectionByPath, productsForCollection, CITY_STATE_GROUPS, CITY_COLLECTIONS } from "@/lib/shopCatalog";
import { SectionVideo } from "@/components/SectionVideo";
import { trackViewItemList } from "@/lib/analytics";
import { OfferCapture } from "@/components/OfferCapture";
import { ExitIntentOffer } from "@/components/ExitIntentOffer";
import { BundleBuilder } from "@/components/BundleBuilder";
import NotFound from "./NotFound";

export default function ShopCollectionPage() {
  const { collectionSlug, citySlug, stateSlug } = useParams();
  const path = stateSlug ? `/shop/state/${stateSlug}` : citySlug ? `/shop/city-series/${citySlug}` : `/shop/${collectionSlug}`;
  const c = collectionByPath(path);

  useEffect(() => {
    if (!c) return;
    window.scrollTo(0, 0);
    setPageMeta(c.title, c.meta_description, { canonicalPath: c.path });
    trackViewItemList(productsForCollection(c), c.slug, c.h1);
    const cleanA = setJsonLd("collection-jsonld", c.jsonld);
    const cleanB = setJsonLd("collection-faq-jsonld", c.faq_ld);
    return () => { cleanA(); cleanB(); };
  }, [path]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!c) return <NotFound />;
  const products = productsForCollection(c);
  const mosaic = products.slice(0, 4);
  const stateVid = c.is_state
    ? String((c.state_slug.split("").reduce((a, ch) => a + ch.charCodeAt(0), 0) % 16) + 1).padStart(2, "0")
    : null;

  return (
    <div className="bg-night min-h-screen" data-testid={`collection-page-${c.slug}`}>
      <div className="px-5 lg:px-10 pt-8">
        <Breadcrumbs light items={c.breadcrumbs} />
      </div>
      <header className="relative overflow-hidden px-5 lg:px-10 pt-8 pb-10 border-b border-white/10">
        {stateVid && (
          <SectionVideo
            src={`/video/run-${stateVid}.mp4`}
            srcMobile={`/video/run-${stateVid}-mobile.mp4`}
            poster={`/video/run-${stateVid}-poster.jpg`}
            opacity={0.2}
            testid="state-hero-video"
          />
        )}
        <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          <Reveal>
            <p className="font-mono-label text-white/40">{c.city_slug ? "CITY SERIES" : c.is_state ? "SHOP BY STATE" : "THE SHOP"}</p>
            <h1 data-testid="collection-h1" className="font-display text-4xl sm:text-5xl lg:text-6xl mt-4 leading-none uppercase">{c.h1}</h1>
            <p data-testid="collection-direct-answer" className="text-white/60 text-sm max-w-2xl mt-5 leading-relaxed">{c.direct_answer}</p>
            <div className="mt-8" data-testid="collection-body">
              {c.body.map((para, i) => (
                <p key={`${i}-${para.slice(0, 30)}`} className="text-white/50 text-sm leading-relaxed mb-4">{para}</p>
              ))}
              {c.directory_path && (
                <Link to={c.directory_path} data-testid="collection-directory-link" className="btn-invert inline-block bg-white text-ink font-mono-label px-6 py-3.5 border border-white hover:bg-transparent hover:text-white mt-2">
                  FIND {c.city_name.toUpperCase()} RUN CLUBS →
                </Link>
              )}
            </div>
          </Reveal>
          {mosaic.length > 0 && (
            <Reveal delay={0.1}>
              <div className="grid grid-cols-2 gap-px bg-white/10 border border-white/10" data-testid="collection-hero-mosaic">
                {mosaic.map((p) => (
                  <Link
                    key={p.slug}
                    to={`/products/${p.url_slug}`}
                    aria-label={p.seo_name}
                    data-testid={`collection-mosaic-${p.slug}`}
                    className="aspect-square block overflow-hidden group relative"
                  >
                    <img
                      src={p.image}
                      alt={p.alt || p.seo_name}
                      loading="lazy"
                      className={`${p.color_art ? "" : "img-bw "}h-full w-full object-cover transition-transform duration-500 group-hover:scale-105`}
                    />
                    <span className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent px-3 pb-2 pt-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <span className="font-mono-label text-white/90 block truncate">{p.seo_name?.toUpperCase()}</span>
                    </span>
                  </Link>
                ))}
              </div>
            </Reveal>
          )}
        </div>
      </header>

      <div className="px-5 lg:px-10 py-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10" data-testid="collection-grid">
          {products.map((p) => (
            <ProductCard key={p.slug} p={p} listId={c.slug} listName={c.h1} />
          ))}
        </div>
      </div>

      {(c.city_slug || c.is_state || c.slug === "city-series") && (
        <section className="px-5 lg:px-10 pb-12" data-testid="collection-bundle-section">
          <BundleBuilder defaultSlug={products.find((p) => ["skyline", "area", "color"].includes(p.kind))?.slug} />
        </section>
      )}

      <section className="px-5 lg:px-10 py-12 border-t border-white/10" data-testid="collection-faq-section">
        <h2 className="font-display text-3xl lg:text-4xl">QUESTIONS, ANSWERED.</h2>
        <div className="mt-6 max-w-3xl">
          {c.faqs.map((f, i) => (
            <details key={f.q} data-testid={`collection-faq-${i}`} className="border-t border-white/10 py-4 group">
              <summary className="font-mono-label text-white/80 cursor-pointer list-none flex justify-between items-center gap-4">
                {f.q}
                <span className="text-white/40 group-open:rotate-45 transition-transform duration-200 shrink-0">+</span>
              </summary>
              <p className="text-white/60 text-sm mt-3 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="px-5 lg:px-10 py-12 border-t border-white/10" data-testid="collection-related">
        <p className="font-mono-label text-white/40">KEEP BROWSING</p>
        <div className="flex flex-wrap gap-2 mt-4">
          {c.related.map(([label, to]) => (
            <Link key={to} to={to} className="font-mono-label border border-white/20 px-4 py-2.5 text-white/70 hover:bg-white hover:text-ink transition-colors duration-200">
              {label.toUpperCase()}
            </Link>
          ))}
          <Link to="/shop" className="font-mono-label border border-white/40 px-4 py-2.5 hover:bg-white hover:text-ink transition-colors duration-200">
            ALL PRODUCTS →
          </Link>
        </div>
        {c.is_state && (
          <div className="flex flex-wrap gap-2 mt-6" data-testid="state-city-links">
            {CITY_COLLECTIONS.filter((cc) => cc.state_slug === c.state_slug).map((cc) => (
              <Link key={cc.path} to={cc.path} className="font-mono-label border border-white/10 px-3 py-2 text-white/50 hover:bg-white hover:text-ink transition-colors duration-200">
                {cc.city_name.toUpperCase()} ({cc.code})
              </Link>
            ))}
          </div>
        )}
        {c.slug === "city-series" && (
          <div className="mt-8 space-y-4" data-testid="city-series-city-links">
            {CITY_STATE_GROUPS.map((g) => (
              <div key={g.slug} className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-4">
                <span className="font-mono-label text-white/30 sm:w-44 sm:pt-2.5 shrink-0">{g.state.toUpperCase()}</span>
                <div className="flex flex-wrap gap-2">
                  {g.cities.map((cc) => (
                    <Link key={cc.path} to={cc.path} className="font-mono-label border border-white/10 px-3 py-2 text-white/50 hover:bg-white hover:text-ink transition-colors duration-200">
                      {cc.city_name.toUpperCase()} ({cc.code})
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="px-5 lg:px-10 py-12 border-t border-white/10">
        <OfferCapture source={`collection-${c.slug}`} />
      </section>
      <ExitIntentOffer source={`exit-collection-${c.slug}`} />
    </div>
  );
}
